/**
 * Cartes de la page Tarifs v2 (maquette /msc : `.introcard`, `.pack`) — SPEC § 4.6, TA1, TA2.
 *
 * Contenu = celui des cartes v1 (TopFeatureCard, PackCard, ContractCard), fusionné par
 * screens/Prices/utils/mergePricingService.ts (config du BO + données Mindbody) : mêmes titres,
 * prix, gabarits, mentions, couleurs de ruban réglées dans le BO. La v2 change la mise en page et
 * la micro-typo (jetons --mmv2-*), jamais la donnée.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { MergedPricingContract, MergedPricingService } from 'screens/Prices/utils/mergePricingService';
import type { PricingRibbon } from 'types/pricing';
import { normalizeHex } from '../../lib/color';
import { Icon } from './icons';
import { useInk, useSitePalette } from './useInk';
import { bandColors, frenchMoney, hasText, mentionsAlma, readableOnSetting, type SiteColor } from './lib';

/**
 * Bandeau haut de carte (ruban du BO). `reserve` : bandeau vide gardé quand une autre carte de la
 * rangée a un ruban, pour aligner les titres (= « serviceRibbonSpacer » v1).
 */
function Band({
  ribbon,
  accentColor,
  fallback,
  reserve,
  className,
}: {
  ribbon: PricingRibbon | null;
  accentColor?: string;
  fallback: string;
  reserve?: boolean;
  className: string;
}) {
  const ink = useInk();
  const palette = useSitePalette();
  if (!ribbon || !hasText(ribbon.text)) {
    return reserve ? <div className={`${className} ${className}--empty`} aria-hidden="true" /> : null;
  }
  const colors = bandColors(ribbon.color, accentColor, fallback, ink, palette);
  return (
    <div className={className} style={colors}>
      {ribbon.text}
    </div>
  );
}

/** Mention Alma d'une carte : le texte du BO, plus le mot « Alma » à la place du logo de la v1. */
function AlmaText({ text }: { text: string }) {
  return (
    <>
      {text}
      {mentionsAlma(text) ? null : (
        <>
          {' '}
          <b className="mmv2-alma">Alma</b>
        </>
      )}
    </>
  );
}

function BuyButton({
  label,
  title,
  variant,
  block,
  style,
  onClick,
}: {
  label: string;
  title: string;
  variant: 'primary' | 'dark' | 'cta' | 'ghost';
  block?: boolean;
  style?: CSSProperties;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`mmv2-btn mmv2-btn--${variant}${block ? ' mmv2-btn--block' : ''} mmv2-prices__buy`}
      // Plusieurs « Acheter » sur la page : le nom de l'offre dans le nom accessible.
      aria-label={title ? `${label} — ${title}` : label}
      style={style}
      onClick={onClick}
    >
      {label}
      <Icon name="right" />
    </button>
  );
}

/** Couleurs de carte réglées dans le BO (fond plein) : fond + texte lisible, en variables locales. */
function solidStyle(
  bgColor: string | undefined,
  textColor: string | undefined,
  ink: string,
  palette: SiteColor[],
): CSSProperties | undefined {
  const solid = readableOnSetting(bgColor, ink, palette);
  if (!solid) return undefined;
  return {
    '--mmv2-card-bg': solid.background,
    '--mmv2-card-fg': normalizeHex(textColor) ?? solid.color,
  } as CSSProperties;
}

// ---------------------------------------------------------------------------------------------
// Blocs « top » (offres mises en avant) — maquette `.introcard`
// ---------------------------------------------------------------------------------------------

export function OfferCard({
  merged,
  almaText,
  reserveBand,
  onBuy,
  place,
}: {
  merged: MergedPricingService;
  almaText: string;
  reserveBand: boolean;
  onBuy?: () => void;
  /** Place dans la grille des blocs « top » (variables --mmv2-o-* : carte seule de sa rangée centrée). */
  place?: CSSProperties;
}) {
  const ink = useInk();
  const palette = useSitePalette();
  const image = merged.image?.url ? merged.image : null;
  const showAlma = merged.almaEnabled && hasText(almaText);
  const hasFine = hasText(merged.validity) || hasText(merged.limitText) || showAlma;
  const canBuy = hasText(merged.ctaLabel) && !!onBuy;
  const hasContent = hasText(merged.title) || hasText(merged.priceLabel) || hasFine || canBuy;
  const solid = solidStyle(merged.bgColor, merged.textColor, ink, palette);
  const cta = readableOnSetting(merged.ctaColor, ink, palette);

  const lines: ReactNode[] = [];
  if (hasText(merged.validity)) lines.push(merged.validity);
  if (showAlma) lines.push(<AlmaText key="alma" text={almaText} />);
  if (hasText(merged.limitText)) lines.push(merged.limitText);

  return (
    <article
      className={`mmv2-offer${solid ? ' mmv2-offer--solid' : ''}${!hasContent ? ' mmv2-offer--image' : ''}`}
      style={place || solid ? { ...place, ...solid } : undefined}
      data-offer={merged.service?.Id ?? undefined}
    >
      <Band ribbon={merged.ribbon} fallback={ink} reserve={reserveBand} className="mmv2-offer__band" />
      {image ? (
        <div className="mmv2-offer__pic">
          <img src={image.url} alt="" loading="lazy" />
        </div>
      ) : null}
      {hasContent ? (
        <div className="mmv2-offer__body">
          {hasText(merged.title) ? (
            <h3 className="mmv2-offer__title" style={merged.titleColor ? { color: merged.titleColor } : undefined}>
              {merged.title}
            </h3>
          ) : null}
          {hasText(merged.priceLabel) && !merged.hidePrice ? (
            <div className="mmv2-offer__price" style={merged.priceColor ? { color: merged.priceColor } : undefined}>
              {frenchMoney(merged.priceLabel)}
            </div>
          ) : null}
          {hasText(merged.pricePerSessionLabel) && !merged.hidePrice ? (
            <div className="mmv2-offer__unit">{frenchMoney(merged.pricePerSessionLabel, { padDecimals: false })}</div>
          ) : null}
          {hasFine ? (
            <p className="mmv2-offer__fine">
              {lines.map((line, i) => (
                <span key={i}>{line}</span>
              ))}
            </p>
          ) : null}
          {canBuy ? (
            <BuyButton
              label={merged.ctaLabel}
              title={merged.title}
              variant={solid ? 'ghost' : 'primary'}
              block
              style={cta ? { background: cta.background, color: cta.color } : undefined}
              onClick={onBuy as () => void}
            />
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/** Photo centrale entre deux blocs « top » (réglage du BO, = TopBlocksRow v1). */
export function OfferImage({ url }: { url: string }) {
  return (
    <div className="mmv2-offer mmv2-offer--image mmv2-offer--central" aria-hidden="true">
      <div className="mmv2-offer__pic">
        <img src={url} alt="" loading="lazy" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Packs — maquette `.pack`
// ---------------------------------------------------------------------------------------------

export function PackCard({
  merged,
  almaText,
  reserveBand,
  onBuy,
}: {
  merged: MergedPricingService;
  almaText: string;
  reserveBand: boolean;
  onBuy?: () => void;
}) {
  const ink = useInk();
  const palette = useSitePalette();
  const image = merged.image?.url ? merged.image : null;
  const showAlma = merged.almaEnabled && hasText(almaText);
  const canBuy = hasText(merged.ctaLabel) && !!onBuy;
  const showPrice = hasText(merged.priceLabel) && !merged.hidePrice;
  const perks: ReactNode[] = [];
  if (hasText(merged.validity)) perks.push(merged.validity);
  if (showAlma) perks.push(<AlmaText key="alma" text={almaText} />);
  const hasContent = hasText(merged.title) || showPrice || perks.length > 0 || canBuy || hasText(merged.limitText);
  const solid = solidStyle(merged.bgColor, merged.textColor, ink, palette);
  // Couleur d'accent de l'offre (BO) : bouton à cette couleur, sinon bouton sombre (maquette).
  const accent = readableOnSetting(merged.accentColor, ink, palette);

  return (
    <article
      className={`mmv2-pack${solid ? ' mmv2-pack--solid' : ''}${!hasContent ? ' mmv2-pack--image' : ''}`}
      style={solid}
      data-offer={merged.service?.Id ?? undefined}
    >
      <Band
        ribbon={merged.ribbon}
        accentColor={merged.accentColor}
        fallback={ink}
        reserve={reserveBand}
        className="mmv2-pack__band"
      />
      {image ? (
        <div className="mmv2-pack__pic">
          <img src={image.url} alt={hasContent ? '' : merged.title || ''} loading="lazy" />
        </div>
      ) : null}
      {hasContent ? (
        <div className="mmv2-pack__body">
          {hasText(merged.title) ? <h3 className="mmv2-pack__title">{merged.title}</h3> : null}
          {showPrice ? (
            merged.pricePerSessionPrice ? (
              <>
                <div className="mmv2-pack__unit">
                  {frenchMoney(merged.pricePerSessionPrice, { padDecimals: false })}
                  {hasText(merged.pricePerSessionSuffix) ? <small> {merged.pricePerSessionSuffix.trim()}</small> : null}
                </div>
                {hasText(merged.priceSubLabel) ? (
                  <div className="mmv2-pack__tot">{frenchMoney(merged.priceSubLabel)}</div>
                ) : null}
              </>
            ) : (
              <div className="mmv2-pack__unit">{frenchMoney(merged.priceLabel)}</div>
            )
          ) : null}
          {perks.length > 0 ? (
            <ul className="mmv2-pack__perks">
              {perks.map((perk, i) => (
                <li key={i}>
                  <Icon name="check" size={15} />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {canBuy || hasText(merged.limitText) ? (
            <div className="mmv2-pack__cta">
              {canBuy ? (
                <BuyButton
                  label={merged.ctaLabel}
                  title={merged.title}
                  variant="dark"
                  style={accent ? { background: accent.background, color: accent.color } : undefined}
                  onClick={onBuy as () => void}
                />
              ) : (
                <span />
              )}
              {hasText(merged.limitText) ? <span className="mmv2-pack__val">{merged.limitText}</span> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------------------------
// Abonnements — maquette `.pack` (subCard)
// ---------------------------------------------------------------------------------------------

export function ContractCard({
  merged,
  reserveBand,
  onSubscribe,
  onDetails,
}: {
  merged: MergedPricingContract;
  reserveBand: boolean;
  onSubscribe: () => void;
  onDetails: () => void;
}) {
  const ink = useInk();
  const image = merged.image?.url ? merged.image : null;
  const perks: string[] = [];
  if (hasText(merged.validity)) perks.push(merged.validity);
  if (hasText(merged.limitText)) perks.push(merged.limitText);
  const showPrice = hasText(merged.priceLabel) && !merged.hidePrice;

  return (
    <article className="mmv2-pack mmv2-pack--contract" data-contract={merged.contract.Id}>
      <Band ribbon={merged.ribbon} fallback={ink} reserve={reserveBand} className="mmv2-pack__band" />
      {image ? (
        <div className="mmv2-pack__pic">
          <img src={image.url} alt="" loading="lazy" />
        </div>
      ) : null}
      <div className="mmv2-pack__body">
        {hasText(merged.title) ? <h3 className="mmv2-pack__title">{merged.title}</h3> : null}
        {showPrice ? (
          <>
            <div className="mmv2-pack__unit">
              {frenchMoney(merged.priceAmount)}
              {hasText(merged.priceSuffix) ? <small> {merged.priceSuffix.trim()}</small> : null}
            </div>
            {hasText(merged.pricePerSessionLabel) ? (
              <div className="mmv2-pack__tot">{frenchMoney(merged.pricePerSessionLabel, { padDecimals: false })}</div>
            ) : null}
          </>
        ) : null}
        {perks.length > 0 ? (
          <ul className="mmv2-pack__perks">
            {perks.map((perk, i) => (
              <li key={i}>
                <Icon name="check" size={15} />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mmv2-pack__cta">
          {hasText(merged.ctaLabel) ? (
            <BuyButton label={merged.ctaLabel} title={merged.title} variant="dark" onClick={onSubscribe} />
          ) : (
            <span />
          )}
          <button type="button" className="mmv2-linkish mmv2-pack__more" onClick={onDetails} aria-haspopup="dialog">
            En savoir plus
            <span className="mmv2-prices__sr"> — {merged.title}</span>
          </button>
        </div>
      </div>
    </article>
  );
}
